// ============================================================================
// projectSchedule: pure date and layout model for the Projects page
// ----------------------------------------------------------------------------
// A farm day is a 'YYYY-MM-DD' string in America/Chicago (todayCentralISO).
// Every calculation here works on the string's calendar day through Date.UTC.
// It never builds a Date in the device's zone, so a phone set to another
// timezone, or a daylight-saving changeover, cannot move a job onto a
// different farm day.
//
// Owner decisions (2026-10-20, projects-20261020-v1/DECISIONS-v1.md):
//   D1 one independent job per calendar row.
//   D2 a scheduled job has an inclusive start and end date; an undated job has
//      neither. The end date defaults to the start date.
//   D4 day columns by default, a Weeks zoom, a Today button, sideways scroll.
//   D5 a done job keeps its bar (grayed, Done badge) and is listed under Done.
//   D6 Remove Dates returns a job to the undated list.
//   D7 an open job whose end date is before the farm's today is Overdue;
//      nothing moves on its own.
// ============================================================================

const DAY_MS = 86_400_000;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const EN_DASH = '\u2013';
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Farm-day arithmetic ─────────────────────────────────────────────────────

// True only for a real calendar date written as 'YYYY-MM-DD' (no time part).
// '2026-02-30' and '2026-13-01' are rejected, as are years outside 1900-9999.
export function isISODate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = ISO_DATE_RE.exec(value);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1900 || mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

function requireISODate(value: string, what: string): void {
  if (!isISODate(value)) throw new RangeError(`${what} must be a YYYY-MM-DD date, got ${JSON.stringify(value)}`);
}

// Days since 1970-01-01 for a farm day. Exact integer for every valid date.
export function dayNumber(iso: string): number {
  requireISODate(iso, 'dayNumber');
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function isoFromDayNumber(n: number): string {
  if (!Number.isInteger(n)) throw new RangeError(`isoFromDayNumber needs an integer, got ${n}`);
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

export function addDaysISO(iso: string, days: number): string {
  return isoFromDayNumber(dayNumber(iso) + days);
}

// Whole days from a to b (positive when b is later).
export function daysBetween(a: string, b: string): number {
  return dayNumber(b) - dayNumber(a);
}

// Length of an inclusive range: Oct 21 – Oct 21 is 1 day, Oct 21 – Oct 28 is 8.
export function inclusiveDayCount(start: string, end: string): number {
  return daysBetween(start, end) + 1;
}

// 0 = Sunday … 6 = Saturday, for the farm day itself (not the device's day).
export function weekdayOf(iso: string): number {
  return (((dayNumber(iso) + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday.
}

export type WeekStart = 0 | 1; // 0 = Sunday, 1 = Monday

// The same day-of-month n calendar months away, clamped to the month's last day
// (Nov 30 minus 3 months is Aug 30; May 31 minus 3 months is Feb 28 or 29).
export function addMonthsISO(iso: string, months: number): string {
  requireISODate(iso, 'addMonthsISO');
  if (!Number.isInteger(months)) throw new RangeError(`addMonthsISO needs whole months, got ${months}`);
  const [y, m, d] = iso.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const ty = first.getUTCFullYear();
  const tm = first.getUTCMonth();
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return isoFromDayNumber(Math.round(Date.UTC(ty, tm, Math.min(d, lastDay)) / DAY_MS));
}

export function startOfWeekISO(iso: string, weekStartsOn: WeekStart = 0): string {
  const back = (weekdayOf(iso) - weekStartsOn + 7) % 7;
  return addDaysISO(iso, -back);
}

export function endOfWeekISO(iso: string, weekStartsOn: WeekStart = 0): string {
  return addDaysISO(startOfWeekISO(iso, weekStartsOn), 6);
}

function minISO(a: string, b: string): string {
  return a <= b ? a : b;
}

function maxISO(a: string, b: string): string {
  return a >= b ? a : b;
}

// ── Job dates (D2, D6) ──────────────────────────────────────────────────────

export type JobDatesError =
  | 'invalid_start'
  | 'invalid_end'
  | 'start_without_end'
  | 'end_without_start'
  | 'end_before_start';

export type JobDatesCheck =
  | {ok: true; startDate: string | null; endDate: string | null}
  | {ok: false; error: JobDatesError};

const blankToNull = (v: string | null | undefined): string | null => (v == null || v === '' ? null : v);

// The one rule for a job's dates, mirrored by the server: both dates or
// neither, each a real farm day, and the end never before the start.
export function checkJobDates(start: string | null | undefined, end: string | null | undefined): JobDatesCheck {
  const s = blankToNull(start);
  const e = blankToNull(end);
  if (s === null && e === null) return {ok: true, startDate: null, endDate: null};
  if (s !== null && !isISODate(s)) return {ok: false, error: 'invalid_start'};
  if (e !== null && !isISODate(e)) return {ok: false, error: 'invalid_end'};
  if (s === null) return {ok: false, error: 'end_without_start'};
  if (e === null) return {ok: false, error: 'start_without_end'};
  if (e < s) return {ok: false, error: 'end_before_start'};
  return {ok: true, startDate: s, endDate: e};
}

// The end date a form shows after the start changes. D2: the end defaults to
// the start, so a one-day job needs one entry. A still-valid end is kept;
// an empty end, or one now before the start, becomes the start.
export function endDateAfterStartChange(
  newStart: string | null | undefined,
  currentEnd: string | null | undefined,
): string | null {
  const s = blankToNull(newStart);
  if (s === null || !isISODate(s)) return blankToNull(currentEnd);
  const e = blankToNull(currentEnd);
  if (e === null || !isISODate(e) || e < s) return s;
  return e;
}

// ── Status (D5, D7) ─────────────────────────────────────────────────────────

export type ScheduleJob = {
  id: string;
  title: string;
  startDate: string | null;
  endDate: string | null;
  completedAt: string | null; // timestamptz; null while open
  createdAt: string; // timestamptz
};

export type JobStatus = 'done' | 'overdue' | 'open';

export function isScheduled(job: Pick<ScheduleJob, 'startDate' | 'endDate'>): boolean {
  return job.startDate !== null && job.endDate !== null;
}

// Derived, never stored: Overdue depends on the farm's today, so it changes at
// Central midnight without anything being written.
export function jobStatus(job: Pick<ScheduleJob, 'endDate' | 'completedAt'>, today: string): JobStatus {
  requireISODate(today, 'today');
  if (job.completedAt) return 'done';
  if (job.endDate !== null && job.endDate < today) return 'overdue';
  return 'open';
}

// The badge word for a job, or null for an open job on schedule. 'Overdue' is
// danger and 'Done' is neutral in statusTone() (an unknown word is neutral,
// which matches the grayed done bar).
export function jobStatusWord(status: JobStatus): 'Done' | 'Overdue' | null {
  if (status === 'done') return 'Done';
  if (status === 'overdue') return 'Overdue';
  return null;
}

// ── Ordering and sections (D5, D8) ──────────────────────────────────────────

const byTitle = (a: ScheduleJob, b: ScheduleJob): number =>
  a.title.localeCompare(b.title, 'en', {sensitivity: 'base'}) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

// Calendar rows: earliest start first, then earliest end, then name.
export function compareCalendarRows(a: ScheduleJob, b: ScheduleJob): number {
  const as = a.startDate ?? '';
  const bs = b.startDate ?? '';
  if (as !== bs) return as < bs ? -1 : 1;
  const ae = a.endDate ?? '';
  const be = b.endDate ?? '';
  if (ae !== be) return ae < be ? -1 : 1;
  return byTitle(a, b);
}

// Undated list: oldest first, so nothing new buries what has waited longest.
export function compareUndated(a: ScheduleJob, b: ScheduleJob): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return byTitle(a, b);
}

// Done section: most recently finished first.
export function compareDone(a: ScheduleJob, b: ScheduleJob): number {
  const ac = a.completedAt ?? '';
  const bc = b.completedAt ?? '';
  if (ac !== bc) return ac > bc ? -1 : 1;
  return byTitle(a, b);
}

export type ProjectSections<J extends ScheduleJob> = {
  calendar: J[]; // every scheduled job, open or done
  undated: J[]; // open jobs without dates
  done: J[]; // every done job, scheduled or not
};

export function partitionJobs<J extends ScheduleJob>(jobs: readonly J[]): ProjectSections<J> {
  const calendar: J[] = [];
  const undated: J[] = [];
  const done: J[] = [];
  for (const job of jobs) {
    if (isScheduled(job)) calendar.push(job);
    else if (!job.completedAt) undated.push(job);
    if (job.completedAt) done.push(job);
  }
  calendar.sort(compareCalendarRows);
  undated.sort(compareUndated);
  done.sort(compareDone);
  return {calendar, undated, done};
}

// ── Calendar rows (D17) and the List mode (D15) ─────────────────────────────

// D17: a done job keeps its grayed row on the Days/Weeks calendar while its end
// date is on or after the farm's today minus 3 calendar months; older done jobs
// live only in the Done section. Unfinished jobs always keep their row.
export const DONE_ON_CALENDAR_MONTHS = 3;

export function calendarJobs<J extends ScheduleJob>(scheduled: readonly J[], today: string): J[] {
  const cutoff = addMonthsISO(today, -DONE_ON_CALENDAR_MONTHS);
  return scheduled.filter((j) => isScheduled(j) && (!j.completedAt || (j.endDate as string) >= cutoff));
}

export type ListGroupKey = 'overdue' | 'today' | 'next7' | 'later';

export const LIST_GROUP_LABELS: Readonly<Record<ListGroupKey, string>> = Object.freeze({
  overdue: 'Overdue',
  today: 'Today',
  next7: 'Next 7 Days',
  later: 'Later',
});

export type ListGroup<J> = {key: ListGroupKey; label: string; jobs: J[]};

// D15: scheduled jobs that are not done, grouped by where they sit against the
// farm's today. Overdue ends before today; Today covers today; Next 7 Days
// starts within the coming week; Later starts after that. Empty groups are kept
// so the caller decides what to show; each group is in calendar order.
export function listGroups<J extends ScheduleJob>(scheduled: readonly J[], today: string): ListGroup<J>[] {
  requireISODate(today, 'today');
  const weekOut = addDaysISO(today, 7);
  const groups: Record<ListGroupKey, J[]> = {overdue: [], today: [], next7: [], later: []};
  for (const job of scheduled) {
    if (!isScheduled(job) || job.completedAt) continue;
    const start = job.startDate as string;
    const end = job.endDate as string;
    if (end < today) groups.overdue.push(job);
    else if (start <= today) groups.today.push(job);
    else if (start <= weekOut) groups.next7.push(job);
    else groups.later.push(job);
  }
  return (Object.keys(groups) as ListGroupKey[]).map((key) => ({
    key,
    label: LIST_GROUP_LABELS[key],
    jobs: groups[key].sort(compareCalendarRows),
  }));
}

// Whole days a job is late (0 when not overdue): the day after the end date is 1.
export function daysOverdue(job: Pick<ScheduleJob, 'endDate' | 'completedAt'>, today: string): number {
  if (job.completedAt || job.endDate === null || job.endDate >= today) return 0;
  return daysBetween(job.endDate, today);
}

// ── Calendar geometry (D4) ──────────────────────────────────────────────────

export type Zoom = 'days' | 'weeks';

// Days: about 4 weeks across a 1366px screen and about a week on a 390px
// phone. Weeks: 40px per week, about 6 months across a 1366px screen.
export const PX_PER_DAY: Readonly<Record<Zoom, number>> = Object.freeze({days: 36, weeks: 40 / 7});

export type CalendarRange = {start: string; end: string; dayCount: number};

export type RangeOptions = {
  pastDays?: number;
  futureDays?: number;
  weekStartsOn?: WeekStart;
};

// The scrollable span: at least pastDays before and futureDays after today,
// widened to every scheduled job, and squared to whole weeks.
export function calendarRange(
  jobs: readonly Pick<ScheduleJob, 'startDate' | 'endDate'>[],
  today: string,
  {pastDays = 35, futureDays = 182, weekStartsOn = 0}: RangeOptions = {},
): CalendarRange {
  requireISODate(today, 'today');
  let start = addDaysISO(today, -pastDays);
  let end = addDaysISO(today, futureDays);
  for (const job of jobs) {
    if (!isScheduled(job)) continue;
    start = minISO(start, job.startDate as string);
    end = maxISO(end, job.endDate as string);
  }
  start = startOfWeekISO(start, weekStartsOn);
  end = endOfWeekISO(end, weekStartsOn);
  return {start, end, dayCount: inclusiveDayCount(start, end)};
}

export type BarGeometry = {
  left: number;
  width: number;
  clippedStart: boolean; // the job starts before the range
  clippedEnd: boolean; // the job ends after the range
};

// Pixel placement of an inclusive job inside an inclusive range. The bar runs
// from the start of its first day to the end of its last day, so a one-day job
// is one full column wide. Null when the job is entirely outside the range.
export function barGeometry(
  startDate: string,
  endDate: string,
  range: Pick<CalendarRange, 'start' | 'end'>,
  pxPerDay: number,
  minWidthPx = 6,
): BarGeometry | null {
  if (endDate < range.start || startDate > range.end) return null;
  const s = maxISO(startDate, range.start);
  const e = minISO(endDate, range.end);
  return {
    left: daysBetween(range.start, s) * pxPerDay,
    width: Math.max(minWidthPx, inclusiveDayCount(s, e) * pxPerDay),
    clippedStart: startDate < range.start,
    clippedEnd: endDate > range.end,
  };
}

export function calendarWidth(range: Pick<CalendarRange, 'dayCount'>, pxPerDay: number): number {
  return range.dayCount * pxPerDay;
}

// The today line sits in the middle of today's column. Null outside the range.
export function todayLineX(
  range: Pick<CalendarRange, 'start' | 'end'>,
  today: string,
  pxPerDay: number,
): number | null {
  if (today < range.start || today > range.end) return null;
  return (daysBetween(range.start, today) + 0.5) * pxPerDay;
}

// Scroll position that opens with today near the left edge, leaving leadDays
// of context before it (Days) or the previous week visible (Weeks).
export function initialScrollLeft(
  range: Pick<CalendarRange, 'start' | 'end'>,
  today: string,
  zoom: Zoom,
  {leadDays = 2, weekStartsOn = 0}: {leadDays?: number; weekStartsOn?: WeekStart} = {},
): number {
  const px = PX_PER_DAY[zoom];
  const anchor = zoom === 'days' ? addDaysISO(today, -leadDays) : startOfWeekISO(addDaysISO(today, -7), weekStartsOn);
  const clamped = minISO(maxISO(anchor, range.start), range.end);
  return daysBetween(range.start, clamped) * px;
}

// ── Header ticks ────────────────────────────────────────────────────────────

export type DayTick = {
  iso: string;
  x: number;
  dayOfMonth: number;
  weekday: number;
  isWeekStart: boolean;
  isMonthStart: boolean;
  isToday: boolean;
};

export function dayTicks(
  range: Pick<CalendarRange, 'start' | 'end'>,
  today: string,
  weekStartsOn: WeekStart = 0,
): DayTick[] {
  const ticks: DayTick[] = [];
  const first = dayNumber(range.start);
  const last = dayNumber(range.end);
  for (let n = first; n <= last; n++) {
    const iso = isoFromDayNumber(n);
    const weekday = weekdayOf(iso);
    ticks.push({
      iso,
      x: (n - first) * PX_PER_DAY.days,
      dayOfMonth: Number(iso.slice(8, 10)),
      weekday,
      isWeekStart: weekday === weekStartsOn,
      isMonthStart: iso.endsWith('-01'),
      isToday: iso === today,
    });
  }
  return ticks;
}

export type WeekTick = {iso: string; x: number; dayOfMonth: number; startsMonth: boolean; containsToday: boolean};

// One tick per week column in the Weeks zoom. startsMonth marks the first week
// column that begins in a new month, which carries the month label.
export function weekTicks(
  range: Pick<CalendarRange, 'start' | 'end'>,
  today: string,
  weekStartsOn: WeekStart = 0,
): WeekTick[] {
  const ticks: WeekTick[] = [];
  let week = startOfWeekISO(range.start, weekStartsOn);
  let prevMonth = '';
  while (week <= range.end) {
    const month = week.slice(0, 7);
    const weekEnd = addDaysISO(week, 6);
    ticks.push({
      iso: week,
      x: daysBetween(range.start, week) * PX_PER_DAY.weeks,
      dayOfMonth: Number(week.slice(8, 10)),
      startsMonth: month !== prevMonth,
      containsToday: today >= week && today <= weekEnd,
    });
    prevMonth = month;
    week = addDaysISO(week, 7);
  }
  return ticks;
}

export type MonthSpan = {iso: string; x: number; width: number; label: string};

// Month bands across the top of either zoom, clipped to the range.
export function monthSpans(range: Pick<CalendarRange, 'start' | 'end'>, pxPerDay: number): MonthSpan[] {
  const spans: MonthSpan[] = [];
  let cursor = range.start;
  while (cursor <= range.end) {
    const [y, m] = cursor.split('-').map(Number);
    const nextMonth = isoFromDayNumber(Math.round(Date.UTC(y, m, 1) / DAY_MS));
    const spanEnd = minISO(addDaysISO(nextMonth, -1), range.end);
    spans.push({
      iso: cursor,
      x: daysBetween(range.start, cursor) * pxPerDay,
      width: inclusiveDayCount(cursor, spanEnd) * pxPerDay,
      label: `${MONTHS_SHORT[m - 1]} ${y}`,
    });
    cursor = nextMonth;
  }
  return spans;
}

// ── Display ─────────────────────────────────────────────────────────────────

// Constitution §5 date wording: 'Oct 6' in the farm's current year, 'Oct 6, 2027'
// otherwise; a range reads 'Oct 6 – Nov 3'; one day reads as a single date.
export function fmtFarmDay(iso: string, today: string): string {
  requireISODate(iso, 'fmtFarmDay');
  const [y, m, d] = iso.split('-').map(Number);
  const day = `${MONTHS_SHORT[m - 1]} ${d}`;
  return y === Number(today.slice(0, 4)) ? day : `${day}, ${y}`;
}

export function fmtJobDates(startDate: string | null, endDate: string | null, today: string): string {
  if (startDate === null || endDate === null) return '';
  if (startDate === endDate) return fmtFarmDay(startDate, today);
  return `${fmtFarmDay(startDate, today)} ${EN_DASH} ${fmtFarmDay(endDate, today)}`;
}
